import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MdLocationOn, MdPeople, MdBusiness } from 'react-icons/md';
import { useAuth } from '../../../Context/AuthContext';
import { toast } from 'react-toastify';
import { getOrganizationAnalyticsByDates, getAllProductivityAnalytics } from "../../../Services/AnalyticsService";
import AnalyticsOrganizationCharts from './AnalyticsOrganizationCharts';
import CustomDatePicker from "../../../Components/Common/CustomDatePicker";
import AnalyticsChatWidget from "../../../Components/Analytics/AnalyticsChatWidget";
import BackButton from '../../../Components/Common/BackButton';

const resolveOrganizationId = (decodedToken) => {
    const possibleValues = [
        decodedToken?.organization_id,
        decodedToken?.OrganizationId,
        decodedToken?.organizationId,
        decodedToken?.org_id,
    ];

    return possibleValues.find((value) => value !== undefined && value !== null && value !== "") || null;
};

// (date range label is computed per-instance inside component)

export default function AnalyticsOrganizationView() {
    const navigate = useNavigate();
    const { token, decodedToken } = useAuth();

    const [organization, setOrganization] = React.useState(null);
    const [productivity, setProductivity] = React.useState([]);
    const [analyticsLoading, setAnalyticsLoading] = React.useState(false);
    const [analyticsError, setAnalyticsError] = React.useState(null);
    const [selectedFacility, setSelectedFacility] = React.useState(null);
    const [selectedProvider, setSelectedProvider] = React.useState(null);

    // compute default date range: today and 7 days ago (Date objects)
    const toYMD = (d) => {
        if (!d) return '';
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const formatDateDisplay = (d) => {
        if (!d) return '';
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
    };

    const defaultEndDate = new Date();
    const defaultStartDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

    const [startDate, setStartDate] = React.useState(() => defaultStartDate);
    const [endDate, setEndDate] = React.useState(() => defaultEndDate);

    const location = useLocation();
    const routeOrganization = React.useMemo(() => location?.state?.organization || null, [location?.state]);
    const organizationId = React.useMemo(() => (
        resolveOrganizationId(decodedToken)
        || routeOrganization?.id
        || routeOrganization?.Id
        || localStorage.getItem('organizationId')
    ), [decodedToken, routeOrganization]);

    React.useEffect(() => {
        if (routeOrganization) {
            setOrganization(routeOrganization);
        }
    }, [routeOrganization]);

    const loadAllProductivity = React.useCallback(async (start = startDate, end = endDate) => {
        if (!token || !organizationId) return;
        setAnalyticsLoading(true);
        setAnalyticsError(null);
        try {
            const s = start ? formatDateDisplay(start instanceof Date ? start : new Date(start)) : '';
            const e = end ? formatDateDisplay(end instanceof Date ? end : new Date(end)) : '';
            const res = await getAllProductivityAnalytics(token, organizationId, s, e);
            setProductivity(Array.isArray(res?.data?.facilities) ? res.data.facilities : []);
        } catch (err) {
            console.error(err);
            setProductivity([]);
            setAnalyticsError(err);
            toast.error('Unable to load productivity analytics');
        } finally {
            setAnalyticsLoading(false);
        }
    }, [token, organizationId, startDate, endDate]);

    const loadFacilityProductivity = React.useCallback(async (facilityName, start = startDate, end = endDate) => {
        if (!token) return;
        setAnalyticsLoading(true);
        setAnalyticsError(null);
        try {
            // service signature: getOrganizationAnalyticsByDates(token, startDate, endDate, facilityName)
            const s = start ? formatDateDisplay(start instanceof Date ? start : new Date(start)) : '';
            const e = end ? formatDateDisplay(end instanceof Date ? end : new Date(end)) : '';
            const res = await getOrganizationAnalyticsByDates(token, s, e, facilityName);
            setProductivity(Array.isArray(res?.data?.facilities) ? res.data.facilities : []);
        } catch (err) {
            console.error(err);
            setProductivity([]);
            setAnalyticsError(err);
            toast.error('Unable to load facility analytics');
        } finally {
            setAnalyticsLoading(false);
        }
    }, [token, startDate, endDate]);

    React.useEffect(() => {
        if (!token || !organizationId) return;

        if (!selectedFacility) {
            loadAllProductivity(startDate, endDate);
        } else {
            loadFacilityProductivity(selectedFacility, startDate, endDate);
        }
        // preserve selectedFacility across date changes
    }, [startDate, endDate, selectedFacility, loadAllProductivity, loadFacilityProductivity, token, organizationId]);

    const totals = React.useMemo(() => {
        return productivity.reduce(
            (acc, item) => {
                const t = item.total_count || {};
                acc.patients += Number(t.patient_count || 0);
                acc.appointments += Number(t.appointment_count || 0);
                acc.visits += Number(t.visit_count || 0);
                return acc;
            },
            { patients: 0, appointments: 0, visits: 0 },
        );
    }, [productivity]);

    const facilityNames = organization?.facility_names || [];
    const providerNames = organization?.provider_names || [];

    // pagination for facilities/providers: 3 rows x 3 cols = 9 items per page
    const FACILITY_PAGE_SIZE = 9;
    const PROVIDER_PAGE_SIZE = 9;
    const [facilityPage, setFacilityPage] = React.useState(0);
    const [providerPage, setProviderPage] = React.useState(0);

    // providers for currently selected facility (do NOT call API)
    const displayedProviders = React.useMemo(() => {
        if (!organization) return [];
        if (selectedFacility) {
            return organization?.facility_providers?.[selectedFacility] || [];
        }
        return providerNames;
    }, [organization, selectedFacility, providerNames]);

    const providerPageItems = React.useMemo(() => {
        const start = providerPage * PROVIDER_PAGE_SIZE;
        return displayedProviders.slice(start, start + PROVIDER_PAGE_SIZE);
    }, [displayedProviders, providerPage]);

    const handleFacilityClick = (name) => {
        const next = selectedFacility === name ? null : name;
        setSelectedProvider(null);
        setSelectedFacility(next);
        setFacilityPage(0);
        setProviderPage(0);
        if (next) {
            // load analytics for selected facility only
            loadFacilityProductivity(next, startDate, endDate);
        } else {
            // reset to all facilities
            loadAllProductivity(startDate, endDate);
        }
    };

    const filteredProductivity = React.useMemo(() => {
        let items = productivity;
        if (selectedFacility) items = items.filter((i) => String(i.facility_name) === String(selectedFacility));
        if (selectedProvider) items = items.filter((i) => {
            if (!i.providers) return false;
            return Object.keys(i.providers || {}).includes(selectedProvider);
        });
        return items;
    }, [productivity, selectedFacility, selectedProvider]);

    return (
        <div className="container-fluid py-4">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                <BackButton onClick={() => navigate(-1)} />

                <AnalyticsChatWidget
                    organizationId={organizationId}
                    organizationName={organization?.organization_name || organization?.OrganizationName}
                />
            </div>

            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

                <div className="row g-4 align-items-center position-relative">
                    <div className="col-12 col-lg-8">
                        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                            <span className="px-3 py-2 rounded-pill fw-semibold bg-success text-white" style={{ fontSize: '0.82rem' }}>
                                Analytics
                            </span>
                            <span className="px-3 py-2 rounded-pill fw-semibold" style={{ fontSize: '0.82rem', backgroundColor: 'rgba(var(--hc-white-rgb), 0.12)', color: 'var(--hc-white)' }}>{organization?.account_name || organization?.AccountName || '—'}</span>
                            <span className="px-3 py-2 rounded-pill fw-semibold bg-info text-white" style={{ fontSize: '0.75rem' }}>
                                {(() => {
                                    if (!startDate && !endDate) return 'All Time';
                                    if (startDate && endDate) return `${formatDateDisplay(startDate)} → ${formatDateDisplay(endDate)}`;
                                    return startDate ? formatDateDisplay(startDate) : formatDateDisplay(endDate) || 'All Time';
                                })()}
                            </span>
                        </div>

                        <h2 className="fw-bold text-white mb-2" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>
                            {organization?.organization_name || organization?.OrganizationName || 'Organization Analytics'}
                        </h2>
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <div className="d-flex align-items-center gap-3 p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.15)' }}>
                                    <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 44, height: 44, backgroundColor: 'var(--hc-surface)' }}>
                                        <MdPeople size={22} color="var(--hc-status-success-text)" />
                                    </div>
                                    <div>
                                        <p className="mb-1 small text-white">Total Patients</p>
                                        <p className="fw-bold mb-0 text-white">{organization?.total_patients ?? 0}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="d-flex align-items-center gap-3 p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.15)' }}>
                                    <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 44, height: 44, backgroundColor: 'var(--hc-surface)' }}>
                                        <MdBusiness size={22} color="var(--hc-icon-blue-alt)" />
                                    </div>
                                    <div>
                                        <p className="mb-1 small text-white">Total Appointments</p>
                                        <p className="fw-bold mb-0 text-white">{organization?.total_appointments ?? 0}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-lg-4">
                        <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
                            <div className="mb-3">
                                <p className="mb-1 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>Organization analytics overview</p>
                                <p className="fw-bold text-white mb-0" style={{ fontSize: '1.3rem', lineHeight: 1.1 }}>Snapshot</p>
                            </div>

                            <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <div className="d-flex align-items-center gap-3 p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.15)' }}>
                                        <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 44, height: 44, backgroundColor: 'var(--hc-surface)' }}>
                                            <MdLocationOn size={22} className="hc-text-primary" />
                                        </div>
                                        <div>
                                            <p className="mb-1 small text-white">Facilities</p>
                                            <p className="fw-bold mb-0 text-white">{facilityNames.length || organization?.facilities_count || 0}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-6">
                                    <div className="d-flex align-items-center gap-3 p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.15)' }}>
                                        <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 44, height: 44, backgroundColor: 'var(--hc-surface)' }}>
                                            <MdPeople size={22} color="var(--hc-status-success-text)" />
                                        </div>
                                        <div>
                                            <p className="mb-1 small text-white">Providers</p>
                                            <p className="fw-bold mb-0 text-white">{providerNames.length || organization?.distinct_total_providers || 0}</p>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary">

                       <div className="d-flex flex-column flex-xl-row flex-wrap justify-content-between align-items-start align-items-xl-center gap-3 mb-4">
    
                                    <h5 className="fw-bold mb-0 d-flex align-items-center gap-2 hc-text-primary">
                                        <MdLocationOn size={20} />
                                        Facilities
                                    </h5>

                                    <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2">

                                        <CustomDatePicker
                                            id="analytics-start-date"
                                            value={startDate}
                                            onChange={(value) =>
                                                setStartDate(
                                                    value ? new Date(value + "T00:00:00") : null
                                                )
                                            }
                                        />

                                        <CustomDatePicker
                                            id="analytics-end-date"
                                            value={endDate}
                                            onChange={(value) =>
                                                setEndDate(
                                                    value ? new Date(value + "T00:00:00") : null
                                                )
                                            }/>

                                        <button
                                            className="btn btn-sm bg-danger text-white fw-semibold rounded-3"
                                            style={{
                                                height: "36px",
                                                minWidth: "80px"
                                            }}
                                            onClick={() => {
                                                setSelectedFacility(null);
                                                setSelectedProvider(null);
                                                setStartDate(defaultStartDate);
                                                setEndDate(defaultEndDate);
                                                loadAllProductivity(defaultStartDate, defaultEndDate);
                                            }}
                                        >
                                            Reset
                                        </button>
                                    </div>

                                </div>

                

                        <div className="row g-3">
                            {(() => {
                                if (!facilityNames.length) return (
                                    <div className="col-12">
                                        <p className="mb-0 text-muted">No facilities found.</p>
                                    </div>
                                );

                                const totalPages = Math.ceil(facilityNames.length / FACILITY_PAGE_SIZE) || 1;
                                const start = facilityPage * FACILITY_PAGE_SIZE;
                                const pageItems = facilityNames.slice(start, start + FACILITY_PAGE_SIZE);

                                return pageItems.map((f) => {
                                    const active = selectedFacility === f;
                                    return (
                                        <div className="col-12 col-md-6 col-xl-4" key={f}>
                                            <div
                                                className={`service-card text-start px-3 py-3 rounded-3 fw-semibold w-100 d-flex align-items-center ${active ? 'active' : 'hc-text-primary'}`}
                                                style={{ minHeight: '72px', transition: 'all .25s ease' }}
                                                onClick={() => { setFacilityPage(0); handleFacilityClick(f); }}
                                            >
                                                <div>
                                                    <div className="small">{f}</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                        {facilityNames.length > FACILITY_PAGE_SIZE && (
                            <div className="d-flex justify-content-end gap-2 mt-3">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    disabled={facilityPage === 0}
                                    onClick={() => setFacilityPage((p) => Math.max(0, p - 1))}
                                >
                                    Prev
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    disabled={facilityPage >= Math.ceil(facilityNames.length / FACILITY_PAGE_SIZE) - 1}
                                    onClick={() => setFacilityPage((p) => p + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h5 className="fw-bold mb-0 d-flex align-items-center gap-2 hc-text-primary"><MdPeople size={22} className="hc-text-primary" /> Providers</h5>
                        </div>

                        <div className="row g-3">
                            {providerPageItems.length ? providerPageItems.map((p) => (
                                <div className="col-12 col-md-6 col-xl-4" key={p}>
                                    <div
                                        className="service-card text-start px-3 py-3 rounded-3 fw-semibold w-100 hc-text-primary d-flex align-items-center"
                                        style={{ minHeight: '72px', transition: 'all .25s ease', cursor: 'default' }}
                                    >
                                        <div>
                                            <div className="small">{p}</div>
                                        </div>
                                    </div>
                                </div>
                            )) : (
                                <div className="col-12">
                                    <p className="mb-0 text-muted">No providers found.</p>
                                </div>
                            )}
                        </div>
                        {displayedProviders.length > PROVIDER_PAGE_SIZE && (
                            <div className="d-flex justify-content-end gap-2 mt-3">
                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                    disabled={providerPage === 0}
                                    onClick={() => setProviderPage((p) => Math.max(0, p - 1))}
                                >
                                    Prev
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                    disabled={providerPage >= Math.ceil(displayedProviders.length / PROVIDER_PAGE_SIZE) - 1}
                                    onClick={() => setProviderPage((p) => p + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="row g-4">
                <div className="col-12">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary">
                        <AnalyticsOrganizationCharts analyticsData={productivity} loading={analyticsLoading} error={analyticsError} />
                    </div>
                </div>
            </div>
        </div>
    );
}